/**
 * Debiteuren Dashboard — Sprint 18 Feb 2026.
 *
 * Centraal scherm voor debiteurenbeheer:
 *  - KPI-cards (totaal openstaand · achterstallig 30/60/90 · Stripe · betalingen vandaag)
 *  - Werkvoorraad met slimme prioriteitskleuren + Customer Score
 *  - Filters (Alle / Te laat / >30 / >60 / >90 / Stripe open / Herinnering vandaag / Betaald vandaag)
 *  - CustomerDetailDrawer met financiële timeline + notities (Sheet, geen popup)
 *  - Directe integratie met Reminder Composer
 *
 * REGEL 2: alle bedragen komen uit backend endpoints (compute_kpis + build_work_queue).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  RefreshCw, Search, Users, AlertTriangle, Clock, CreditCard, Loader2,
  TrendingUp, ChevronRight, X, Mail, Phone, StickyNote,
  CheckCircle2, FileText, Coins, Send, Info,
} from "lucide-react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Badge } from "../../ui/badge";
import { Textarea } from "../../ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../../ui/sheet";
import {
  AdminPageHeader, AdminKpiCard, AdminFilterTabs, AdminSectionCard,
  AdminEmptyState, AdminSkeleton, AdminSearchInput, AdminStatusBadge,
} from "../ui";
import ReminderComposer from "./ReminderComposer";

// ─────────────────────────────────────────────────────────────
// Utils
// ─────────────────────────────────────────────────────────────
const fmtMoney = (v, cur = "EUR") =>
  `${cur} ${Number(v || 0).toLocaleString("nl-NL", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;

const fmtInt = (v) => Number(v || 0).toLocaleString("nl-NL");

const PRIORITY_STYLES = {
  green: "bg-emerald-100 text-emerald-800 border-emerald-300",
  yellow: "bg-yellow-100 text-yellow-800 border-yellow-300",
  orange: "bg-orange-100 text-orange-900 border-orange-300",
  red: "bg-red-100 text-red-800 border-red-300",
  darkred: "bg-red-900 text-white border-red-950",
};

const SCORE_STYLES = {
  emerald: "bg-emerald-500 text-white",
  blue: "bg-blue-500 text-white",
  amber: "bg-amber-500 text-white",
  red: "bg-red-600 text-white",
};

const FILTERS = [
  { key: "all", label: "Alle" },
  { key: "overdue", label: "Te laat" },
  { key: "overdue_30", label: "> 30d" },
  { key: "overdue_60", label: "> 60d" },
  { key: "overdue_90", label: "> 90d" },
  { key: "stripe_open", label: "Stripe open" },
  { key: "stripe_expired", label: "Stripe verlopen" },
  { key: "reminder_today", label: "Herinnering vandaag" },
  { key: "paid_today", label: "Betaald vandaag" },
];

// ─────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────
export default function DebiteurenDashboard() {
  const { user } = useAuth();
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [queue, setQueue] = useState({ items: [], total: 0 });
  const [queueLoading, setQueueLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [composerState, setComposerState] = useState({ open: false, customerId: null, invoiceId: null });

  const loadKpis = useCallback(async () => {
    try {
      const r = await adminApi.get("/admin/debtor-dashboard/kpis");
      setKpis(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  const loadQueue = useCallback(async () => {
    setQueueLoading(true);
    try {
      const params = new URLSearchParams({
        filter, search, limit: "50", sort_by: "priority",
      });
      const r = await adminApi.get(`/admin/debtor-dashboard/work-queue?${params}`);
      setQueue(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setQueueLoading(false);
    }
  }, [filter, search]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadKpis(), loadQueue()]);
      setLoading(false);
    })();
  }, [loadKpis, loadQueue]);

  const refresh = () => Promise.all([loadKpis(), loadQueue()]);

  return (
    <div className="space-y-5" data-testid="debiteuren-dashboard">
      {/* Header */}
      <AdminPageHeader
        icon={Users}
        title="Debiteuren"
        subtitle={kpis?.as_of ? `Peildatum ${kpis.as_of}` : "Uw ochtendoverzicht voor debiteurenbeheer"}
        actions={
          <Button
            variant="outline"
            onClick={refresh}
            disabled={loading || queueLoading}
            className="rounded-full border-slate-200 hover:border-brand-500 hover:text-brand-500"
            data-testid="debiteuren-refresh"
          >
            {loading || queueLoading
              ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
              : <RefreshCw className="w-4 h-4 mr-1.5" />}
            Vernieuwen
          </Button>
        }
      />

      {/* KPI grid */}
      <KpiGrid kpis={kpis} loading={loading && !kpis} />

      {/* Filter + search */}
      <AdminSectionCard padded={false} testid="debiteuren-filter-card">
        <div className="px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="debiteuren-filters">
          <AdminFilterTabs
            items={FILTERS}
            value={filter}
            onChange={setFilter}
            testidPrefix="debiteuren-filter"
          />
          <div className="ml-auto flex items-center gap-3">
            <AdminSearchInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Zoek klant, bedrijf, factuurnr…"
              testid="debiteuren-search"
            />
            <div className="text-xs text-ink-500 whitespace-nowrap font-medium" data-testid="debiteuren-count">
              {queue.total} klanten
            </div>
          </div>
        </div>
      </AdminSectionCard>

      {/* Work Queue */}
      <WorkQueueTable
        loading={queueLoading}
        items={queue.items}
        onOpenCustomer={setSelectedCustomerId}
        onOpenComposer={(customerId, invoiceId) => setComposerState({ open: true, customerId, invoiceId })}
      />

      {/* Customer Detail Sheet */}
      {selectedCustomerId && (
        <CustomerDetailDrawer
          customerId={selectedCustomerId}
          open={!!selectedCustomerId}
          onClose={() => setSelectedCustomerId(null)}
          onOpenComposer={(customerId, invoiceId) =>
            setComposerState({ open: true, customerId, invoiceId })
          }
        />
      )}

      {/* Reminder Composer */}
      <ReminderComposer
        open={composerState.open}
        onOpenChange={(v) => setComposerState((s) => ({ ...s, open: v }))}
        customerId={composerState.customerId}
        focusInvoiceId={composerState.invoiceId}
        currentUserEmail={user?.email || ""}
        onSent={() => refresh()}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// KPI grid
// ─────────────────────────────────────────────────────────────
function KpiGrid({ kpis, loading }) {
  if (loading) {
    return <AdminSkeleton variant="kpi-grid" count={12} />;
  }
  const k = kpis || {};
  const cur = k.currency || "EUR";
  const cards = [
    { label: "Totaal openstaand",       value: fmtMoney(k.total_outstanding, cur),          icon: Coins,        tone: "brand",   testid: "kpi-total-outstanding" },
    { label: "Openstaande facturen",    value: fmtInt(k.open_invoices_count),               icon: FileText,     tone: "slate",   testid: "kpi-open-invoices" },
    { label: "Klanten open",            value: fmtInt(k.customers_with_open),               icon: Users,        tone: "slate",   testid: "kpi-customers-open" },
    { label: "Herinneringen vandaag",   value: fmtInt(k.reminders_due_today),               icon: Send,         tone: "amber",   testid: "kpi-reminders-today" },
    { label: "> 30 dagen te laat",      value: fmtMoney(k.overdue_30_amount, cur),          icon: Clock,        tone: "orange",  testid: "kpi-overdue-30" },
    { label: "> 60 dagen te laat",      value: fmtMoney(k.overdue_60_amount, cur),          icon: Clock,        tone: "red",     testid: "kpi-overdue-60" },
    { label: "> 90 dagen te laat",      value: fmtMoney(k.overdue_90_amount, cur),          icon: AlertTriangle,tone: "darkred", testid: "kpi-overdue-90" },
    { label: "Betalingen vandaag",      value: fmtMoney(k.payments_received_today, cur),    icon: CheckCircle2, tone: "emerald", testid: "kpi-payments-today" },
    { label: "Stripe vandaag",          value: fmtMoney(k.stripe_payments_today, cur),      icon: CreditCard,   tone: "blue",    testid: "kpi-stripe-today" },
    { label: "Open Stripe-links",       value: fmtInt(k.stripe_open_count),                 icon: CreditCard,   tone: "blue",    testid: "kpi-stripe-open" },
    {
      label: "Gem. betalingstermijn",
      value: k.avg_pay_days_this_month != null ? `${k.avg_pay_days_this_month} dgn` : "—",
      icon: Clock, tone: "slate", testid: "kpi-avg-paydays",
    },
    {
      label: "Incasso-% maand",
      value: k.collection_rate_this_month_pct != null ? `${k.collection_rate_this_month_pct}%` : "—",
      icon: TrendingUp, tone: "emerald", testid: "kpi-collection-rate",
    },
  ];
  // KPI-tones passen direct op AdminKpiCard TONE_STYLES; geen mapping nodig.
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
      {cards.map((c) => (
        <AdminKpiCard
          key={c.label}
          label={c.label}
          value={c.value}
          icon={c.icon}
          tone={c.tone}
          testid={c.testid}
        />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Work Queue Table
// ─────────────────────────────────────────────────────────────
function WorkQueueTable({ loading, items, onOpenCustomer, onOpenComposer }) {
  if (loading) {
    return <AdminSkeleton variant="table" rows={6} cols={7} />;
  }
  if (!items?.length) {
    return (
      <AdminEmptyState
        icon={Users}
        title="Geen klanten in deze weergave"
        description="Pas een ander filter toe of maak een nieuwe factuur aan."
        testid="debiteuren-empty"
      />
    );
  }
  return (
    <AdminSectionCard padded={false} testid="debiteuren-workqueue-card">
      <div className="overflow-x-auto" data-testid="debiteuren-workqueue">
        <div className="grid grid-cols-12 gap-2 px-5 py-3 bg-slate-50/80 border-b border-slate-200 text-[10px] uppercase tracking-[0.14em] text-ink-500 font-chivo font-bold">
          <div className="col-span-1">Prioriteit</div>
          <div className="col-span-3">Klant</div>
          <div className="col-span-1 text-center">Score</div>
          <div className="col-span-1 text-right">Facturen</div>
          <div className="col-span-2 text-right">Openstaand</div>
          <div className="col-span-1 text-right">Te laat</div>
          <div className="col-span-2">Laatst</div>
          <div className="col-span-1 text-right">Acties</div>
        </div>
        {items.map((it, i) => (
          <WorkQueueRow
            key={it.customer_id || `noid-${i}`}
            it={it}
            onOpenCustomer={onOpenCustomer}
            onOpenComposer={onOpenComposer}
          />
        ))}
      </div>
    </AdminSectionCard>
  );
}

function WorkQueueRow({ it, onOpenCustomer, onOpenComposer }) {
  const prio = it.priority || { color: "green", label: "?" };
  const score = it.score || { letter: "?", color: "amber" };
  // Map priority-kleur naar AdminStatusBadge tone (green→success, yellow→warning etc.)
  const prioTone = { green: "success", yellow: "warning", orange: "warning", red: "danger", darkred: "critical" }[prio.color] || "neutral";
  const lastActivity = (() => {
    const r = it.latest_reminder;
    const p = it.latest_payment;
    if (r && (!p || (r.sent_at || "") > (p.payment_date || ""))) {
      return { kind: "reminder", ts: (r.sent_at || "").slice(0, 10), label: r.reminder_type };
    }
    if (p) return { kind: "payment", ts: (p.payment_date || "").slice(0, 10), label: `${p.currency || "EUR"} ${Number(p.amount).toFixed(0)}` };
    return null;
  })();
  const stripeCls = {
    active: "bg-blue-100 text-blue-800 border-blue-200",
    completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
    expired: "bg-slate-100 text-slate-600 border-slate-200",
    none: "bg-slate-50 text-slate-400 border-slate-200",
  };
  return (
    <div
      className="grid grid-cols-12 gap-2 px-5 py-3 border-b border-slate-100 last:border-b-0 hover:bg-brand-50/40 items-center text-sm transition-colors"
      data-testid={`debiteuren-row-${it.customer_id || "no-cid"}`}
    >
      <div className="col-span-1" data-testid="row-priority">
        <AdminStatusBadge tone={prioTone} label={prio.label} size="xs" />
      </div>
      <div className="col-span-3 min-w-0">
        <button
          type="button"
          onClick={() => onOpenCustomer(it.customer_id)}
          disabled={!it.customer_id}
          className="text-left w-full group"
          data-testid="row-open-customer"
        >
          <div className="font-semibold text-brand-900 truncate group-hover:text-brand-500 transition-colors">
            {it.customer_name}
          </div>
          {it.company_name && (
            <div className="text-[11px] text-ink-500 truncate">{it.company_name}</div>
          )}
          {it.customer_email && (
            <div className="text-[11px] text-ink-400 truncate">{it.customer_email}</div>
          )}
        </button>
      </div>
      <div className="col-span-1 text-center">
        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-chivo font-bold ${SCORE_STYLES[score.color] || "bg-slate-300 text-white"}`} data-testid="row-score">
          {score.letter}
        </span>
      </div>
      <div className="col-span-1 text-right font-mono-num tabular-nums font-semibold text-brand-900">
        {fmtInt(it.invoice_count)}
      </div>
      <div className="col-span-2 text-right font-mono-num tabular-nums font-bold text-brand-900">
        {fmtMoney(it.total_open, it.currency)}
      </div>
      <div className="col-span-1 text-right font-mono-num tabular-nums">
        <span className={it.days_overdue > 0 ? "text-red-700 font-bold" : "text-ink-400"}>
          {it.days_overdue}d
        </span>
      </div>
      <div className="col-span-2 text-xs text-ink-500 space-y-1 min-w-0">
        {lastActivity ? (
          <div className="truncate">
            {lastActivity.kind === "reminder"
              ? <Send className="w-3 h-3 inline mr-1 text-amber-600" />
              : <Coins className="w-3 h-3 inline mr-1 text-emerald-600" />}
            {lastActivity.ts} · {lastActivity.label}
          </div>
        ) : (
          <div className="text-ink-300 italic">geen activiteit</div>
        )}
        {it.stripe_status !== "none" && (
          <Badge variant="outline" className={`text-[9px] ${stripeCls[it.stripe_status] || stripeCls.none}`}>
            <CreditCard className="w-2.5 h-2.5 mr-0.5" /> {it.stripe_status}
          </Badge>
        )}
      </div>
      <div className="col-span-1 text-right">
        <Button
          size="sm"
          onClick={() => onOpenComposer(it.customer_id, null)}
          disabled={!it.customer_id}
          className="h-8 text-xs bg-brand-900 hover:bg-brand-700 text-white rounded-full px-3 shadow-brand-soft"
          data-testid="row-composer-btn"
        >
          <Send className="w-3 h-3 mr-1" /> Composer
        </Button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Customer Detail Drawer (Sheet)
// ─────────────────────────────────────────────────────────────
function CustomerDetailDrawer({ customerId, open, onClose, onOpenComposer }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState("");
  const [notesSaving, setNotesSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get(`/admin/debtor-dashboard/customer/${customerId}`);
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { if (customerId) load(); }, [customerId, load]);

  const saveNote = async () => {
    if (!newNote.trim()) return;
    setNotesSaving(true);
    try {
      await adminApi.post(
        `/admin/debtor-dashboard/customer/${customerId}/notes`,
        { customer_id: customerId, text: newNote, kind: "algemeen" },
      );
      setNewNote("");
      toast.success("Notitie toegevoegd");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setNotesSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent className="w-[95vw] sm:max-w-2xl overflow-y-auto p-0" data-testid="debiteuren-detail-drawer">
        <SheetHeader className="px-5 py-3 border-b border-slate-200 bg-slate-50 sticky top-0 z-10">
          <SheetTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            {loading ? "Laden…" : data?.customer?.name || "Klantdetail"}
          </SheetTitle>
          <SheetDescription className="text-xs text-slate-600">
            Financieel dossier · timeline · notities
          </SheetDescription>
        </SheetHeader>
        {loading || !data ? (
          <div className="p-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Laden…</div>
        ) : (
          <div className="p-5 space-y-5">
            {/* Klantinformatie + score */}
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-0.5">
                <div className="text-lg font-semibold text-slate-900">{data.customer.name}</div>
                {data.customer.company_name && (
                  <div className="text-sm text-slate-600">{data.customer.company_name}</div>
                )}
                <div className="text-xs text-slate-500 flex items-center gap-3 flex-wrap mt-1">
                  {data.customer.email && (
                    <a href={`mailto:${data.customer.email}`} className="hover:underline">
                      <Mail className="w-3 h-3 inline mr-1" />{data.customer.email}
                    </a>
                  )}
                  {data.customer.phone && (
                    <a href={`tel:${data.customer.phone}`} className="hover:underline">
                      <Phone className="w-3 h-3 inline mr-1" />{data.customer.phone}
                    </a>
                  )}
                  {data.customer.city && <span>{data.customer.city}, {data.customer.country}</span>}
                </div>
              </div>
              <div className="text-center">
                <span className={`inline-flex items-center justify-center w-10 h-10 rounded-full font-bold text-lg ${SCORE_STYLES[data.score?.color] || "bg-slate-300"}`} data-testid="detail-score">
                  {data.score?.letter}
                </span>
                <div className="text-[10px] text-slate-500 mt-1">{data.score?.score}/100</div>
              </div>
            </div>
            {/* Openstaand + snelle actie */}
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 flex items-center justify-between gap-2 flex-wrap">
              <div>
                <div className="text-[10px] uppercase text-slate-500">Openstaand saldo</div>
                <div className="text-xl font-bold text-slate-900" data-testid="detail-total-open">
                  {fmtMoney(data.customer_total_open, data.open_invoices[0]?.currency || "EUR")}
                </div>
                <div className="text-xs text-slate-500">
                  {data.open_invoices.length} openstaande factuur{data.open_invoices.length === 1 ? "" : "en"}
                </div>
              </div>
              <Button
                onClick={() => onOpenComposer(customerId, null)}
                className="bg-brand-900 text-white hover:bg-brand-700 rounded-full shadow-brand-soft"
                data-testid="detail-open-composer"
              >
                <Send className="w-4 h-4 mr-1" /> Reminder Composer
              </Button>
            </div>

            {/* Openstaande facturen */}
            {data.open_invoices?.length > 0 && (
              <section>
                <SectionHeader label="Openstaande facturen" />
                <div className="border border-slate-200 rounded-md overflow-hidden">
                  {data.open_invoices.map((inv) => (
                    <div key={inv.id} className="flex items-center gap-3 px-3 py-2 border-b border-slate-100 last:border-b-0 text-sm hover:bg-slate-50">
                      <FileText className="w-3.5 h-3.5 text-slate-400" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{inv.invoice_number}</div>
                        <div className="text-xs text-slate-500">
                          {inv.invoice_date} · vervalt {inv.due_date || "—"}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-slate-900">
                          {fmtMoney(inv.amount_due, inv.currency)}
                        </div>
                        <div className="text-[10px] text-slate-500 uppercase">{inv.financial_status}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => onOpenComposer(customerId, inv.id)}
                        className="text-xs px-2 py-1 border border-slate-200 rounded hover:bg-slate-100"
                        data-testid={`detail-composer-${inv.invoice_number}`}
                      >
                        Composer
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Timeline */}
            <section>
              <SectionHeader label={`Timeline (${data.timeline?.length || 0})`} />
              <TimelineList events={data.timeline || []} />
            </section>

            {/* Notities */}
            <section>
              <SectionHeader label={`Interne notities (${data.notes?.length || 0})`} icon={StickyNote} />
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Nieuwe notitie (alleen intern zichtbaar)…"
                    className="flex-1 min-h-[60px] text-sm"
                    data-testid="detail-note-input"
                  />
                  <Button
                    onClick={saveNote}
                    disabled={!newNote.trim() || notesSaving}
                    className="self-start bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="detail-note-save"
                  >
                    {notesSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : "Toevoegen"}
                  </Button>
                </div>
                {(data.notes || []).map((n) => (
                  <div key={n.id} className="border border-yellow-200 bg-yellow-50 rounded p-2 text-xs" data-testid={`detail-note-${n.id}`}>
                    <div className="flex items-center justify-between text-[10px] text-slate-500 mb-1">
                      <span>{(n.created_at || "").slice(0, 16).replace("T", " ")} · {n.author_email || "?"}</span>
                      <Badge variant="outline" className="text-[9px]">{n.kind}</Badge>
                    </div>
                    <div className="text-slate-800 whitespace-pre-wrap">{n.text}</div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function SectionHeader({ label, icon: Icon }) {
  return (
    <div className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-600 mb-2 pb-1 border-b border-slate-200">
      {Icon && <Icon className="w-3 h-3" />}
      {label}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Timeline
// ─────────────────────────────────────────────────────────────
const EVENT_ICONS = {
  slate: "bg-slate-100 text-slate-700",
  blue: "bg-blue-100 text-blue-700",
  emerald: "bg-emerald-100 text-emerald-700",
  amber: "bg-amber-100 text-amber-700",
  yellow: "bg-yellow-100 text-yellow-700",
  sky: "bg-sky-100 text-sky-700",
};

function TimelineList({ events }) {
  if (!events?.length) {
    return <div className="text-sm text-slate-400 italic py-4">Nog geen financiële activiteit.</div>;
  }
  return (
    <div className="space-y-2" data-testid="detail-timeline">
      {events.map((e, i) => {
        const ts = (e.ts || "").replace("T", " ").slice(0, 16);
        return (
          <div key={`${e.type}-${i}`} className="flex gap-3 items-start" data-testid={`timeline-event-${e.type}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${EVENT_ICONS[e.color] || EVENT_ICONS.slate}`}>
              {e.icon === "credit-card" && <CreditCard className="w-4 h-4" />}
              {e.icon === "mail" && <Mail className="w-4 h-4" />}
              {e.icon === "coins" && <Coins className="w-4 h-4" />}
              {e.icon === "check-circle" && <CheckCircle2 className="w-4 h-4" />}
              {e.icon === "x-circle" && <X className="w-4 h-4" />}
              {e.icon === "sticky-note" && <StickyNote className="w-4 h-4" />}
              {(!e.icon || ["file-plus", "send", "activity"].includes(e.icon)) && <FileText className="w-4 h-4" />}
            </div>
            <div className="flex-1 min-w-0 bg-white border border-slate-200 rounded p-2">
              <div className="flex items-center justify-between gap-2 text-xs">
                <span className="font-medium text-slate-900 truncate">{e.title}</span>
                <span className="text-slate-500 whitespace-nowrap">{ts}</span>
              </div>
              {e.description && (
                <div className="text-xs text-slate-600 mt-0.5">{e.description}</div>
              )}
              {e.actor && (
                <div className="text-[10px] text-slate-400 mt-1">door {e.actor}</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
